import { Divider, Alert, AlertTitle, lighten, Typography, Tooltip, IconButton, Link } from '@mui/material';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { useTranslation } from 'react-i18next';
import { AccessTime, ContentCopy, DoNotDisturb, NewReleases } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';

export type AutogiroMandateAlertMessageProps = {
  item: AutogiroMandateResponse;
};

export const AutogiroMandateAlertMessage = (props: AutogiroMandateAlertMessageProps) => {
  const { item } = props;
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();

  const [tooltipText, setTooltipText] = useState<string>(String(t('invoice:createAutogiroMandate.copyLink')));
  const { currentUser } = useGlobals();

  const hasBankAccount = item.clearingNr && item.clearingNr.length > 0 && item.accountNr && item.accountNr.length > 0;

  const link = () => {
    let setLink = `https://forms.foreningssupport.se/BankAccount/ChooseBank?guid=${item.guid}&cId=${currentUser?.custName}`;

    if (hasBankAccount) {
      setLink = `https://forms.foreningssupport.se/AutogiroApproval?guid=${item.guid}&cId=${currentUser?.custName}`;
    }

    return setLink;
  };

  const getHeader = (enm) => t(`invoice:autogiroMandateStatus.${AutogiroMandateStatusEnum[enm]}Header`);
  const getDescription = (enm) => t(`invoice:autogiroMandateStatus.${AutogiroMandateStatusEnum[enm]}Description`);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(link());
      if (isMounted()) setTooltipText(t('invoice:createAutogiroMandate.linkCopied'));
    } catch (err) {
      if (isMounted()) setTooltipText(t('invoice:createAutogiroMandate.failedToCopyLink'));
    }
  };

  return (
    <>
      <Divider />
      {item.status === AutogiroMandateStatusEnum.New && item.isSigned === false && (
        <Alert severity='warning' square icon={<NewReleases />} sx={{ backgroundColor: (thm) => lighten(thm.palette.warning.main, 0.8) }}>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.New)}</AlertTitle>
          <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
            {hasBankAccount ? t(`invoice:autogiroMandateStatus.NewDescriptionMissingSign`) : getDescription(AutogiroMandateStatusEnum.New)}
          </Typography>
          <Typography variant='body2'>
            <Link href={link()} target='_blank' rel='noopener noreferrer'>
              {link()}
            </Link>
            <Tooltip title={tooltipText} onClose={() => setTooltipText(t('invoice:createAutogiroMandate.copyLink'))}>
              <IconButton sx={{ ml: 1 }} onClick={handleCopyClick}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Typography>
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.Approved && (
        <Alert severity='success' square>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.Approved)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.Approved)}
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.NotApproved && (
        <Alert severity='error' square>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.NotApproved)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.NotApproved)}
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.Canceled && (
        <Alert severity='error' square icon={<DoNotDisturb />} sx={{ backgroundColor: (thm) => thm.palette.grey[200] }}>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.Canceled)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.Canceled)}
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.PendingApproval && (
        <Alert severity='success' square icon={<AccessTime />}>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.PendingApproval)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.PendingApproval)}
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.PendingCancel && (
        <Alert severity='info' square icon={<AccessTime />} sx={{ backgroundColor: (thm) => thm.palette.grey[200] }}>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.PendingCancel)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.PendingCancel)}
        </Alert>
      )}
      {item.status === AutogiroMandateStatusEnum.Error && (
        <Alert severity='error' square>
          <AlertTitle>{getHeader(AutogiroMandateStatusEnum.Error)}</AlertTitle>
          {getDescription(AutogiroMandateStatusEnum.Error)}
        </Alert>
      )}
      <Divider />
    </>
  );
};
